import React, { useEffect, useMemo, useState } from "react";
import type { CampaignMetricsEntry } from "../types";
import { SectionCard } from "./SectionLayout";

type FeatureKey = "reengagementSequence" | "noChatIntro" | "reachout" | "emailReply";
type SortKey = "sent" | "openRate" | "clickRate" | "replyRate" | "name";

const FEATURE_FILTERS: Array<{ id: FeatureKey; label: string }> = [
  { id: "reengagementSequence", label: "Reengagement" },
  { id: "noChatIntro", label: "No-chat Intro" },
  { id: "reachout", label: "Reach-out Rules" },
  { id: "emailReply", label: "Email Reply" },
];

const SORT_OPTIONS: Array<{ value: SortKey; label: string }> = [
  { value: "sent", label: "Emails sent" },
  { value: "openRate", label: "Open rate" },
  { value: "clickRate", label: "Click rate" },
  { value: "replyRate", label: "Reply rate" },
  { value: "name", label: "Name" },
];

interface BatchComparisonSectionProps {
  campaignMetrics: CampaignMetricsEntry[];
  isLoading?: boolean;
}

export const BatchComparisonSection: React.FC<BatchComparisonSectionProps> = ({
  campaignMetrics,
  isLoading = false,
}) => {
  const [search, setSearch] = useState("");
  const [featureFilters, setFeatureFilters] = useState<
    Record<FeatureKey, boolean>
  >({
    reengagementSequence: false,
    noChatIntro: false,
    reachout: false,
    emailReply: false,
  });
  const [sortKey, setSortKey] = useState<SortKey>("sent");
  const [pageSize, setPageSize] = useState(8);
  const [page, setPage] = useState(1);
  const [hiddenIds, setHiddenIds] = useState<Record<string, boolean>>({});

  const activeFeatureFilters = useMemo(
    () =>
      FEATURE_FILTERS.filter((feature) => featureFilters[feature.id]).map(
        (feature) => feature.id
      ),
    [featureFilters]
  );

  useEffect(() => {
    setPage(1);
  }, [
    search,
    pageSize,
    featureFilters.reengagementSequence,
    featureFilters.noChatIntro,
    featureFilters.reachout,
    featureFilters.emailReply,
  ]);

  const filteredMetrics = useMemo(() => {
    const normalizedQuery = search.trim().toLowerCase();
    return campaignMetrics.filter((metric) => {
      if (hiddenIds[metric.campaignId]) return false;
      const matchesSearch =
        !normalizedQuery ||
        metric.campaignName.toLowerCase().includes(normalizedQuery) ||
        metric.campaignId.toLowerCase().includes(normalizedQuery);

      if (!matchesSearch) return false;

      const matchesFeatures =
        !activeFeatureFilters.length ||
        activeFeatureFilters.every((feature) => metric.features?.[feature]);

      return matchesFeatures;
    });
  }, [
    activeFeatureFilters,
    campaignMetrics,
    hiddenIds,
    search,
  ]);

  const sortedMetrics = useMemo(() => {
    const sorted = [...filteredMetrics];
    sorted.sort((a, b) => {
      switch (sortKey) {
        case "openRate":
          return b.openRate - a.openRate;
        case "clickRate":
          return b.clickRate - a.clickRate;
        case "replyRate":
          return b.replyRate - a.replyRate;
        case "name":
          return a.campaignName.localeCompare(b.campaignName);
        case "sent":
        default:
          return b.sent - a.sent;
      }
    });
    return sorted;
  }, [filteredMetrics, sortKey]);

  const totalPages = Math.max(1, Math.ceil(sortedMetrics.length / pageSize));

  useEffect(() => {
    setPage((current) => Math.min(current, totalPages));
  }, [totalPages]);

  const safePage = Math.min(page, totalPages);
  const startIndex = (safePage - 1) * pageSize;
  const visibleMetrics = sortedMetrics.slice(startIndex, startIndex + pageSize);
  const hiddenCount = useMemo(
    () => Object.values(hiddenIds).filter(Boolean).length,
    [hiddenIds]
  );

  const toggleFeature = (featureId: FeatureKey) => {
    setFeatureFilters((current) => ({
      ...current,
      [featureId]: !current[featureId],
    }));
  };

  const handleHide = (campaignId: string) => {
    setHiddenIds((current) => ({ ...current, [campaignId]: true }));
  };

  const clearHidden = () => setHiddenIds({});

  const palette = ["bg-emerald-50 text-emerald-700", "bg-blue-50 text-blue-700", "bg-amber-50 text-amber-700", "bg-sky-50 text-sky-700"];
  const featureLabels: Record<string, string> = {
    reengagementSequence: "Reengagement sequence",
    noChatIntro: "No-chat intro",
    reachout: "Reach-out rules",
    emailReply: "Email reply",
  };

  return (
    <SectionCard
      title="Batch Comparison"
      description="Search, filter, and prune older experiments without loading more data."
    >
      <div className="space-y-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search campaigns"
              className="w-full min-w-[220px] rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
            />
            <select
              value={sortKey}
              onChange={(event) => setSortKey(event.target.value as SortKey)}
              className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
            >
              {SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  Sort by: {option.label}
                </option>
              ))}
            </select>
            <select
              value={pageSize}
              onChange={(event) => setPageSize(Number(event.target.value))}
              className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
            >
              {[6, 8, 12, 20].map((size) => (
                <option key={size} value={size}>
                  {size} per page
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {FEATURE_FILTERS.map((feature) => {
              const isActive = featureFilters[feature.id];
              return (
                <button
                  key={feature.id}
                  type="button"
                  onClick={() => toggleFeature(feature.id)}
                  className={`rounded-full px-3 py-2 text-xs font-semibold transition ${
                    isActive
                      ? "bg-[var(--primary-color)] text-white shadow"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {feature.label}
                </button>
              );
            })}
            {hiddenCount > 0 ? (
              <button
                type="button"
                onClick={clearHidden}
                className="rounded-full border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 shadow-sm hover:border-[var(--primary-color)] hover:text-[var(--primary-color)]"
              >
                Show hidden ({hiddenCount})
              </button>
            ) : null}
          </div>
        </div>

        {isLoading ? (
          <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-5 text-center text-sm text-gray-500">
            Loading campaign metrics...
          </div>
        ) : sortedMetrics.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-5 text-center text-sm text-gray-500">
            {campaignMetrics.length === 0
              ? "No campaign metrics available yet."
              : "No batch comparisons match your filters."}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {visibleMetrics.map((metric) => {
                const enabledFeatures = Object.entries(metric.features ?? {}).filter(
                  ([, enabled]) => Boolean(enabled)
                );
                return (
                  <div
                    key={metric.campaignId}
                    className="relative rounded-2xl border border-gray-200 bg-gray-50 p-4"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-sm font-semibold text-gray-900">
                          {metric.campaignName}
                        </div>
                        <div className="mt-1 text-xs text-gray-500">
                          {metric.audienceCount} unique users · {metric.sent} emails sent
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleHide(metric.campaignId)}
                        className="rounded-lg border border-transparent px-2 py-1 text-[11px] font-semibold text-gray-500 transition hover:border-gray-200 hover:bg-white hover:text-gray-700"
                      >
                        Hide
                      </button>
                    </div>

                    {enabledFeatures.length ? (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {enabledFeatures.map(([featureKey], index) => (
                          <span
                            key={`${metric.campaignId}-${featureKey}`}
                            className={`inline-flex items-center rounded-full px-3 py-1 text-[11px] font-semibold shadow ${
                              palette[index % palette.length]
                            }`}
                          >
                            {featureLabels[featureKey] ?? featureKey}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <div className="mt-3 text-xs text-gray-500">
                        No automations configured yet.
                      </div>
                    )}

                    <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
                      <div>
                        <div className="text-gray-500">Open rate</div>
                        <div className="text-sm font-semibold text-gray-900">
                          {metric.openRate.toFixed(1)}%
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-500">Click-through rate</div>
                        <div className="text-sm font-semibold text-gray-900">
                          {metric.clickRate.toFixed(1)}%
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-500">Reply rate</div>
                        <div className="text-sm font-semibold text-gray-900">
                          {metric.replyRate.toFixed(1)}%
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-500">Unsubscribe rate</div>
                        <div className="text-sm font-semibold text-gray-900">
                          {metric.unsubscribeRate.toFixed(1)}%
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-500">Opened</div>
                        <div className="text-sm font-semibold text-gray-900">
                          {metric.opened}/{metric.sent}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-500">Clicked</div>
                        <div className="text-sm font-semibold text-gray-900">
                          {metric.clicked}/{metric.sent}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex flex-col items-start justify-between gap-3 border-t border-gray-200 pt-4 text-sm text-gray-600 md:flex-row md:items-center">
              <div>
                Showing {sortedMetrics.length === 0 ? 0 : startIndex + 1}-
                {Math.min(sortedMetrics.length, startIndex + pageSize)} of{" "}
                {sortedMetrics.length} campaigns
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPage((current) => Math.max(1, current - 1))}
                  disabled={safePage === 1}
                  className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-semibold text-gray-700 disabled:cursor-not-allowed disabled:opacity-50 hover:border-[var(--primary-color)] hover:text-[var(--primary-color)]"
                >
                  Previous
                </button>
                <div className="text-xs font-semibold text-gray-700">
                  Page {safePage} of {totalPages}
                </div>
                <button
                  type="button"
                  onClick={() =>
                    setPage((current) => Math.min(totalPages, current + 1))
                  }
                  disabled={safePage >= totalPages}
                  className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-semibold text-gray-700 disabled:cursor-not-allowed disabled:opacity-50 hover:border-[var(--primary-color)] hover:text-[var(--primary-color)]"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </SectionCard>
  );
};
